import {
  GoogleMap,
  useJsApiLoader,
  DirectionsService,
  DirectionsRenderer,
} from "@react-google-maps/api";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";

import NoRunningLateDataFound from "./NoRunningLateDataFound";
import AppLocumIcon from "../../../../assets/images/favicon.ico";
import RunningLateIcon from "../../../../assets/images/running_late.svg";
import {
  GOOGLE__MAP_API_KEY,
  GOOGLE_MAP_API_ID,
} from "../../../../Constants/common";
import { markerLate } from "../../../../utilities/utils";
import TableLoader from "../../TableLoader";

const mapContainerStyle = {
  width: "100%",
  height: "450px",
};

const RunningLateMapListing = () => {
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [markers, setMarkers] = useState([]);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [directionRequest, setDirectionRequest] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE__MAP_API_KEY,
    libraries: markerLate,
  });

  const { runningLateContract, isLoading } = useSelector(
    (state) => state.clientContractDetail,
  );
  const { user } = useSelector((state) => state.clientProfile);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (runningLateContract && Object.keys(runningLateContract).length > 0) {
      const key = Object.keys(runningLateContract)[0];
      const { candidate_lat, candidate_lon, client_lat, client_lon } =
        runningLateContract[key];

      const parsedClientLat = parseFloat(client_lat);
      const parsedClientLon = parseFloat(client_lon);
      const parsedCandidateLat = parseFloat(candidate_lat);
      const parsedCandidateLon = parseFloat(candidate_lon);

      const markersData = [];

      if (!isNaN(parsedCandidateLat) && !isNaN(parsedCandidateLon)) {
        markersData.push({
          lat: parsedCandidateLat,
          lng: parsedCandidateLon,
          label: "Candidate Location",
        });
      }
      if (!isNaN(parsedClientLat) && !isNaN(parsedClientLon)) {
        markersData.push({
          lat: parsedClientLat,
          lng: parsedClientLon,
          label: user?.client_branch_name || "Your Location",
        });
      }

      if (markersData.length > 0) {
        setMarkers(markersData);
        setCenter(markersData[0]);

        if (markersData.length === 2) {
          setDirectionRequest({
            origin: { lat: parsedCandidateLat, lng: parsedCandidateLon },
            destination: { lat: parsedClientLat, lng: parsedClientLon },
            travelMode: "DRIVING",
          });
        }
      }
    }
  }, [runningLateContract, user]);

  const handleMapLoad = (map) => {
    mapRef.current = map;

    if (
      window.google &&
      window.google.maps &&
      window.google.maps.marker &&
      window.google.maps.marker.AdvancedMarkerElement
    ) {
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((marker) => {
        const iconSrc =
          marker.label === "Candidate Location"
            ? RunningLateIcon
            : AppLocumIcon;
        const iconImg = document.createElement("img");
        iconImg.src = iconSrc;
        iconImg.style.width = "55px";
        iconImg.style.height = "55px";

        const markerContainer = document.createElement("div");
        markerContainer.style.position = "relative";
        markerContainer.appendChild(iconImg);

        const labelDiv = document.createElement("div");
        labelDiv.style.position = "absolute";
        labelDiv.style.transform = "translateX(-30%) translateY(-260%)";
        labelDiv.style.backgroundColor = "white";
        labelDiv.style.color = "black";
        labelDiv.style.fontSize = "14px";
        labelDiv.style.fontWeight = "bold";
        labelDiv.style.padding = "13px";
        labelDiv.style.borderRadius = "5px";
        labelDiv.style.whiteSpace = "nowrap";
        labelDiv.style.boxShadow = "0 1px 4px rgba(0,0,0,0.3)";
        labelDiv.innerText = marker?.label || "Location";

        markerContainer.appendChild(labelDiv);

        new window.google.maps.marker.AdvancedMarkerElement({
          map: mapRef.current,
          position: { lat: marker.lat, lng: marker.lng },
          title: marker?.label || "Location",
          content: markerContainer,
        });

        bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
      });

      // Only fit bounds if there are two markers
      if (markers.length === 2) {
        mapRef.current.fitBounds(bounds);

        // Allow manual zoom after fitting bounds
        window.google.maps.event.addListenerOnce(
          mapRef.current,
          "bounds_changed",
          () => {
            if (mapRef.current.getZoom() > 10) {
              mapRef.current.setZoom(10);
            }
          },
        );
      } else if (markers.length === 1) {
        // Set default zoom if only one marker exists
        mapRef.current.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
        mapRef.current.setZoom(15);
      }
    }
  };

  const handleDirectionsCallback = (response) => {
    if (response !== null && directionsResponse === null) {
      // Only update if not already set
      if (response.status === "OK") {
        setDirectionsResponse(response);
      } else {
        console.error("Directions request failed due to:", response.status);
      }
    }
  };

  const candidateCoordinatesValid =
    runningLateContract &&
    Object.keys(runningLateContract).length > 0 &&
    runningLateContract[Object.keys(runningLateContract)[0]]?.candidate_lon !==
      "0.0";

  if (loadError) return <p>Error loading maps</p>;
  if (!isLoaded || isLoading) return <TableLoader rows={5} />;

  return (
    <>
      <div className="contractDetailTitle mb-4 flex gap-16">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold leading-[normal] text-txtColor">
            Running late Reason :
          </h3>
          <p className="text-[14px] font-bold leading-[normal] text-black">
            {runningLateContract && Object.keys(runningLateContract).length > 0
              ? runningLateContract[Object.keys(runningLateContract)[0]]
                  ?.reason || "No reason provided"
              : "No reason provided"}
          </p>
        </div>

        {candidateCoordinatesValid ? (
          <>
            <div className="flex items-center gap-2">
              <h3 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                Duration :
              </h3>
              <p className="text-[14px] font-bold leading-[normal] text-black">
                {runningLateContract &&
                Object.keys(runningLateContract).length > 0
                  ? runningLateContract[Object.keys(runningLateContract)[0]]
                      ?.duration || "-"
                  : "No Duration provided"}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <h3 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                Distance :
              </h3>
              <p className="text-[14px] font-bold leading-[normal] text-black">
                {runningLateContract &&
                Object.keys(runningLateContract).length > 0
                  ? runningLateContract[Object.keys(runningLateContract)[0]]
                      ?.distance || "-"
                  : "No Distance provided"}
              </p>
            </div>
          </>
        ) : null}
      </div>
      {/* </div> */}

      {!candidateCoordinatesValid ? (
        <NoRunningLateDataFound />
      ) : (
        <div className="runningLateMapListing">
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={15}
              onLoad={handleMapLoad}
              options={{
                mapId: GOOGLE_MAP_API_ID,
                zoomControl: true,
              }}
            >
              {directionRequest && directionsResponse === null && (
                <DirectionsService
                  options={directionRequest}
                  callback={handleDirectionsCallback}
                />
              )}
              {directionsResponse && (
                <DirectionsRenderer
                  directions={directionsResponse}
                  options={{
                    polylineOptions: {
                      strokeColor: "#0939AB",
                      strokeOpacity: 0.8,
                      strokeWeight: 5,
                    },
                    suppressMarkers: true,
                  }}
                />
              )}
            </GoogleMap>
          )}
        </div>
      )}
    </>
  );
};

export default RunningLateMapListing;
